import React, { useEffect, useState, useMemo, useCallback } from 'react';
import LicStateEdit from './LicStateEdit';
import StateRequirementEdit from './StateRequirementEdit';
import { getAllLicStates } from '../api/States';
import api from '../utils/api';
import { getAllProfessions, Profession } from '../api/Professions';
import {
  getAllStateRequirements,
  createStateRequirement,
  updateStateRequirement,
  deleteStateRequirement,
  IStateRequirement,
  ICategory
} from '../api/StateRequirements';
import { updateCategory } from '../api/Categories';
import { LicState } from '../types';

const StateManagement: React.FC = () => {
  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit' | null>(null);
  const [modalCategory, setModalCategory] = useState<ICategory | null>(null);
  const [modalReqId, setModalReqId] = useState<string | null>(null);
  const [modalError, setModalError] = useState('');

  // Modal close handler
  const closeModal = useCallback(() => {
    setShowModal(false);
    setModalCategory(null);
    setModalReqId(null);
    setModalError('');
  }, []);

  // Handlers for Add/Edit modal
  const openAddModal = useCallback((requirementId?: string) => {
    setModalMode('add');
    setModalCategory({ 
      _id: '',
      categoryTag: '', 
      name: '',
      stateID: 0,
      professionID: '',
      minCredits: 0, 
      maxCredits: undefined, 
      description: '' 
    } as ICategory);
    setShowModal(true);
    setModalReqId(requirementId || null);
    setModalError('');
  }, []);

  const openEditModal = useCallback((req: IStateRequirement, cat: ICategory) => {
    setModalMode('edit');
    setModalCategory({ ...cat });
    setShowModal(true);
    setModalReqId(req._id || null);
    setModalError('');
  }, []);

  // Data & Loading State
  const [states, setStates] = useState<LicState[]>([]);
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [requirements, setRequirements] = useState<IStateRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');


  // Selection State
  const [selectedProfessionID, setSelectedProfessionID] = useState<string | null>(null);
  const [selectedStateID, setSelectedStateID] = useState<number | null>(null);

  // Modal state for LicState and StateRequirement editing
  const [editLicState, setEditLicState] = useState<LicState | null>(null);
  const [editStateRequirement, setEditStateRequirement] = useState<IStateRequirement | null>(null);

  // Edit LicState button handler
  const handleEditLicState = (licState: LicState) => {
    setEditLicState(licState);
  };

  // Edit StateRequirement button handler
  const handleEditStateRequirement = (stateReq: IStateRequirement) => {
    setEditStateRequirement(stateReq);
  };

  // Save handlers for modals
  const handleSaveLicState = async (updated: LicState) => {
    try {
      // PATCH or PUT to /api/states/:id (assuming RESTful API)
      await api.put(`/api/states/${updated._id}`, updated);
      setStates(prev => prev.map(s => s.stateID === updated.stateID ? updated : s));
      setEditLicState(null);
    } catch (err: any) {
      alert('Failed to save LicState: ' + (err.response?.data?.message || err.message));
    }
  };
  const handleSaveStateRequirement = async (updated: IStateRequirement) => {
    try {
      if (!updated._id) throw new Error('Missing StateRequirement ID');
      await api.put(`/api/state-requirements/${updated._id}`, updated);
      setRequirements(prev => prev.map(r => r._id === updated._id ? updated : r));
      setEditStateRequirement(null);
    } catch (err: any) {
      alert('Failed to save StateRequirement: ' + (err.response?.data?.message || err.message));
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [statesRes, profsRes, reqsRes] = await Promise.all([
          getAllLicStates(),
          getAllProfessions(),
          getAllStateRequirements()
        ]);
        console.log('State Management - Fetched data:', {
          states: statesRes.length,
          professions: profsRes.length,
          requirements: reqsRes.length
        });
        console.log('All requirements:', reqsRes);
        console.log('RE requirements:', reqsRes.filter((r: any) => r.professionID === 'RE'));
        setStates(statesRes);
        setProfessions(profsRes);
        setRequirements(reqsRes);
        setError('');
      } catch (err: any) {
        console.error('State Management fetch error:', err);
        setError('Failed to load state/profession requirements.');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Memoize the grouping of requirements to prevent re-calculation on every render
  const groupedRequirements = useMemo(() => {
    const grouped: Record<number, Record<string, IStateRequirement[]>> = {};
    requirements.forEach(req => {
      if (!grouped[req.stateID]) grouped[req.stateID] = {};
      if (!grouped[req.stateID][req.professionID]) grouped[req.stateID][req.professionID] = [];
      grouped[req.stateID][req.professionID].push(req);
    });
    console.log('Grouped requirements:', grouped);
    console.log('StateID 32:', grouped[32]);
    console.log('StateID 33:', grouped[33]);
    return grouped;
  }, [requirements]);

  // OPTIMIZATION: Wrap delete logic in useCallback and update state locally
  const handleDeleteCategory = useCallback(async (reqId: string, categoryTag: string) => {
    if (!window.confirm(`Are you sure you want to delete the category "${categoryTag}"?`)) {
      return;
    }

    const reqToUpdate = requirements.find(r => r._id === reqId);
    if (!reqToUpdate) {
      alert('Error: Could not find the requirement to update.');
      return;
    }

    try {
      const updatedCategories = reqToUpdate.categoryIDs.filter(c => c.categoryTag !== categoryTag);
      await updateStateRequirement(reqId, { categoryIDs: updatedCategories });

      // Update local state instead of re-fetching
      setRequirements(prevReqs =>
        prevReqs.map(r =>
          r._id === reqId ? { ...r, categoryIDs: updatedCategories } : r
        )
      );
    } catch (err: any) {
      alert('Delete failed: ' + (err.message || 'Unknown error'));
    }
  }, [requirements]); // Dependency on `requirements` is correct here

  // --- Render Logic ---

  // IMPROVEMENT: Extract complex JSX into a variable for clarity
  // --- Editable LU Requirements Panel State ---
  const [editForm, setEditForm] = React.useState<IStateRequirement | null>(null);
  const [editError, setEditError] = React.useState('');
  const [isDirty, setIsDirty] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);

  React.useEffect(() => {
    if (!selectedStateID || !selectedProfessionID) {
      setEditForm(null);
      setIsDirty(false);
      setEditError('');
      return;
    }
    const reqs = groupedRequirements[selectedStateID]?.[selectedProfessionID] || [];
    if (reqs.length === 0) {
      setEditForm(null);
      setIsDirty(false);
      setEditError('');
      return;
    }
    setEditForm({ ...reqs[0] });
    setIsDirty(false);
    setEditError('');
  }, [selectedStateID, selectedProfessionID, groupedRequirements]);

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setEditForm(f => {
      if (!f) return f;
      const v = type === 'number' ? (value === '' ? undefined : Number(value)) : value;
      const updated = { ...f, [name]: v };
      setIsDirty(true);
      return updated;
    });
  };

  const handleUpdate = async () => {
    if (!editForm) return;
    // Basic validation
    if (!editForm.effectiveDate) return setEditError('Effective date is required.');
    if (!editForm.totalCreditsRequired || isNaN(Number(editForm.totalCreditsRequired))) return setEditError('Total credits required is required and must be a number.');
    if (!editForm.cycleLengthYears || isNaN(Number(editForm.cycleLengthYears))) return setEditError('Cycle length (years) is required and must be a number.');
    setEditError('');
    setIsUpdating(true);
    try {
      if (!editForm._id || typeof editForm._id !== 'string') throw new Error('Missing StateRequirement ID');
      await updateStateRequirement(editForm._id, editForm);
      setRequirements(prev => prev.map(r => r._id === editForm._id ? { ...editForm } : r));
      setIsDirty(false);
    } catch (err: any) {
      setEditError('Failed to update: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsUpdating(false);
    }
  };

  const renderRequirementsTable = () => {
    if (!selectedStateID || !selectedProfessionID) return null;
    const reqs = groupedRequirements[selectedStateID]?.[selectedProfessionID] || [];
    if (reqs.length === 0) {
      return (
        <div className="alert alert-info">
          <h6>No requirements found for this profession in this state.</h6>
          <p className="mb-2">Click "Add Category" above to create the first requirement and category.</p>
          <small className="text-muted">Note: A base requirement document will be created with default values (2-year cycle, 0 credits). You can edit these after creation.</small>
        </div>
      );
    }
    const allCats = reqs.flatMap(req => req.categoryIDs.map(cat => ({ req, cat })));
    return (
      <div className="mb-4 p-3 border rounded bg-light">
  <h6 className="fw-bold">General Requirements</h6>
        {editForm && (
          <div className="mb-2 row g-2">
            <div className="col-md-4">
              <label className="form-label">Effective Date</label>
              <input className="form-control" name="effectiveDate" type="date" value={editForm.effectiveDate ? String(editForm.effectiveDate).slice(0,10) : ''} onChange={handleFieldChange} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Expiry Date</label>
              <input className="form-control" name="expiryDate" type="date" value={editForm.expiryDate ? String(editForm.expiryDate).slice(0,10) : ''} onChange={handleFieldChange} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Total Credits Required</label>
              <input className="form-control" name="totalCreditsRequired" type="number" value={editForm.totalCreditsRequired ?? ''} onChange={handleFieldChange} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Cycle Length (years)</label>
              <input className="form-control" name="cycleLengthYears" type="number" value={editForm.cycleLengthYears ?? ''} onChange={handleFieldChange} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Allow Carryover</label>
              <select className="form-select" name="allowCarryover" value={editForm.allowCarryover ? 'true' : 'false'} onChange={e => setEditForm(f => f ? { ...f, allowCarryover: e.target.value === 'true' } : f)}>
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Carryover Max Credits</label>
              <input className="form-control" name="carryoverMaxCredits" type="number" value={editForm.carryoverMaxCredits ?? ''} onChange={handleFieldChange} />
            </div>
            
            {/* Fixed Cycle Dates Section */}
            <div className="col-12">
              <hr className="my-3" />
              <h6 className="fw-bold">Fixed Cycle Dates</h6>
              <small className="text-muted">Configure if this state has standardized renewal periods (e.g., calendar year)</small>
            </div>
            <div className="col-md-4">
              <label className="form-label">Has Fixed Cycle Dates</label>
              <select 
                className="form-select" 
                name="hasFixedCycleDates" 
                value={editForm.hasFixedCycleDates ? 'true' : 'false'} 
                onChange={e => setEditForm(f => f ? { ...f, hasFixedCycleDates: e.target.value === 'true' } : f)}
              >
                <option value="false">No (User-defined dates)</option>
                <option value="true">Yes (Fixed dates)</option>
              </select>
            </div>
            
            {editForm.hasFixedCycleDates && (
              <>
                <div className="col-md-4">
                  <label className="form-label">Start Month</label>
                  <select 
                    className="form-select" 
                    name="fixedCycleStartMonth" 
                    value={editForm.fixedCycleStartMonth ?? ''} 
                    onChange={e => setEditForm(f => f ? { ...f, fixedCycleStartMonth: e.target.value ? parseInt(e.target.value) : undefined } : f)}
                  >
                    <option value="">Select month...</option>
                    <option value="1">January</option>
                    <option value="2">February</option>
                    <option value="3">March</option>
                    <option value="4">April</option>
                    <option value="5">May</option>
                    <option value="6">June</option>
                    <option value="7">July</option>
                    <option value="8">August</option>
                    <option value="9">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Start Day</label>
                  <input 
                    className="form-control" 
                    name="fixedCycleStartDay" 
                    type="number" 
                    min="1" 
                    max="31" 
                    value={editForm.fixedCycleStartDay ?? ''} 
                    onChange={e => setEditForm(f => f ? { ...f, fixedCycleStartDay: e.target.value ? parseInt(e.target.value) : undefined } : f)}
                    placeholder="1-31"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">End Month</label>
                  <select 
                    className="form-select" 
                    name="fixedCycleEndMonth" 
                    value={editForm.fixedCycleEndMonth ?? ''} 
                    onChange={e => setEditForm(f => f ? { ...f, fixedCycleEndMonth: e.target.value ? parseInt(e.target.value) : undefined } : f)}
                  >
                    <option value="">Select month...</option>
                    <option value="1">January</option>
                    <option value="2">February</option>
                    <option value="3">March</option>
                    <option value="4">April</option>
                    <option value="5">May</option>
                    <option value="6">June</option>
                    <option value="7">July</option>
                    <option value="8">August</option>
                    <option value="9">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">End Day</label>
                  <input 
                    className="form-control" 
                    name="fixedCycleEndDay" 
                    type="number" 
                    min="1" 
                    max="31" 
                    value={editForm.fixedCycleEndDay ?? ''} 
                    onChange={e => setEditForm(f => f ? { ...f, fixedCycleEndDay: e.target.value ? parseInt(e.target.value) : undefined } : f)}
                    placeholder="1-31"
                  />
                </div>
                <div className="col-md-12">
                  <label className="form-label">Cycle Date Notes</label>
                  <input 
                    className="form-control" 
                    name="cycleDateNotes" 
                    value={editForm.cycleDateNotes || ''} 
                    onChange={handleFieldChange}
                    placeholder="e.g., Texas architects must renew annually on the calendar year (January 1 - December 31)"
                  />
                  <small className="text-muted">Explain the fixed cycle policy to users</small>
                </div>
                {editForm.fixedCycleStartMonth && editForm.fixedCycleStartDay && editForm.fixedCycleEndMonth && editForm.fixedCycleEndDay && (
                  <div className="col-12">
                    <div className="alert alert-info">
                      <strong>Preview:</strong> Cycles will run from {
                        new Date(2024, editForm.fixedCycleStartMonth - 1, editForm.fixedCycleStartDay).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
                      } to {
                        new Date(2024, editForm.fixedCycleEndMonth - 1, editForm.fixedCycleEndDay).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
                      } each year
                    </div>
                  </div>
                )}
              </>
            )}
            
            <div className="col-12"><hr className="my-3" /></div>
            
            <div className="col-md-8">
              <label className="form-label">Notes</label>
              <input className="form-control" name="notes" value={editForm.notes || ''} onChange={handleFieldChange} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Created At</label>
              <input className="form-control" value={editForm.createdAt ? new Date(editForm.createdAt).toLocaleString() : '-'} readOnly />
            </div>
            <div className="col-md-6">
              <label className="form-label">Updated At</label>
              <input className="form-control" value={editForm.updatedAt ? new Date(editForm.updatedAt).toLocaleString() : '-'} readOnly />
            </div>
          </div>
        )}
        {editError && <div className="alert alert-danger mt-2">{editError}</div>}
        <button className="btn btn-primary mt-3" disabled={!isDirty || isUpdating} onClick={handleUpdate}>
          {isUpdating ? (
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          ) : null}
          Update
        </button>
        <hr />
        <h6 className="fw-bold mt-3">Category Requirements</h6>
        <table className="table table-bordered table-sm align-middle mt-3">
          <thead>
            <tr>
              <th>Category</th><th>Min Credits</th><th>Max Credits</th><th>Description</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {allCats.map(({ req, cat }) => (
              <tr key={`${req._id}-${cat.categoryTag}`}>
                <td>{cat.categoryTag}</td>
                <td>{cat.minCredits}</td>
                <td>{cat.maxCredits ?? '-'}</td>
                <td>{cat.description ?? '-'}</td>
                <td>
                  <button className="btn btn-sm btn-outline-primary me-1" onClick={() => openEditModal(req, cat)}>Edit</button>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteCategory(req._id!, cat.categoryTag)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };


  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>State LU Requirements Management</h2>
        <button 
          className="btn btn-primary" 
          onClick={() => window.location.reload()}
        >
          🔄 Refresh Data
        </button>
      </div>
      <div className="mb-3">
        <label htmlFor="stateSelect" className="form-label fw-bold">Select State:</label>
        <select
          id="stateSelect"
          className="form-select"
          value={selectedStateID ?? ''}
          onChange={e => {
            setSelectedStateID(e.target.value ? Number(e.target.value) : null);
            setSelectedProfessionID(null);
            console.log('Selected stateID:', e.target.value);
          }}
        >
          <option value="">-- Choose a state --</option>
          {states
            .filter(state => groupedRequirements[state.stateID] && Object.keys(groupedRequirements[state.stateID]).length > 0)
            .map(state => (
              <option key={state.stateID} value={state.stateID}>
                {state.state} ({state.stateID})
              </option>
            ))}
        </select>
      </div>

      {selectedStateID && (
        <>
          <div className="mb-3">
            <label htmlFor="professionSelect" className="form-label fw-bold">Select Profession:</label>
            <select
              id="professionSelect"
              className="form-select"
              value={selectedProfessionID ?? ''}
              onChange={e => {
                const profID = e.target.value || null;
                setSelectedProfessionID(profID);
                console.log('Selected professionID:', profID);
                console.log('Requirements for this combo:', groupedRequirements[selectedStateID!]?.[profID!]);
              }}
            >
              <option value="">-- Choose a profession --</option>
              {professions
                .filter(prof => prof.isActive) // Show all active professions
                .sort((a, b) => a.name.localeCompare(b.name)) // Sort professions alphabetically
                .map(prof => {
                  const hasRequirements = groupedRequirements[selectedStateID]?.[prof.professionID]?.length > 0;
                  return (
                    <option key={prof.professionID} value={prof.professionID}>
                      {prof.name} ({prof.professionID}) {hasRequirements ? '✓' : ''}
                    </option>
                  );
                })}
            </select>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-bold" style={{ fontSize: '1.5rem' }}>CEC Requirements for Profession</span>
            <button 
              className="btn btn-success btn-sm" 
              onClick={() => {
                // Get the first requirement for this profession/state
                if (selectedStateID && selectedProfessionID) {
                  const reqs = groupedRequirements[selectedStateID]?.[selectedProfessionID] || [];
                  const reqId = reqs[0]?._id || '';
                  openAddModal(reqId);
                }
              }} 
              disabled={!selectedProfessionID}
            >
              Add Category
            </button>
          </div>

          {renderRequirementsTable()}
        </>
      )}

      {/* LicState Edit Modal */}
      {editLicState && (
        <LicStateEdit
          licState={editLicState}
          onSave={handleSaveLicState}
          onCancel={() => setEditLicState(null)}
        />
      )}

      {/* StateRequirement Edit Modal */}
      {editStateRequirement && (
        <StateRequirementEdit
          stateRequirement={editStateRequirement}
          onSave={handleSaveStateRequirement}
          onCancel={() => setEditStateRequirement(null)}
        />
      )}

      {/* Modal for add/edit category */}
      {showModal && (
        <div className="modal d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.3)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{modalMode === 'add' ? 'Add Category' : 'Edit Category'}</h5>
                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>
              <div className="modal-body">
                {modalError && <div className="alert alert-danger">{modalError}</div>}
                <div className="mb-2">
                  <label className="form-label">Category Tag</label>
                  {modalMode === 'add' ? (
                    <select
                      className="form-select"
                      value={modalCategory?.categoryTag || ''}
                      onChange={e => setModalCategory(c => c ? { ...c, categoryTag: e.target.value } : null)}
                      required
                    >
                      <option value="">Select a category...</option>
                      {(() => {
                        // Use selectedProfessionID directly instead of looking up from requirement
                        // This ensures we always show tags for the currently selected profession
                        const profession = professions.find(p => p.professionID === selectedProfessionID);
                        
                        return profession?.validCategoryTags?.map(tag => (
                          <option key={tag.tag} value={tag.tag}>
                            {tag.displayName} ({tag.tag})
                          </option>
                        )) || <option disabled>No tags available for this profession</option>;
                      })()}
                    </select>
                  ) : (
                    <input
                      className="form-control"
                      value={modalCategory?.categoryTag || ''}
                      readOnly
                    />
                  )}
                  <small className="text-muted">
                    {(() => {
                      // Use selectedProfessionID directly to ensure we show description for correct profession
                      const profession = professions.find(p => p.professionID === selectedProfessionID);
                      const selectedTag = profession?.validCategoryTags?.find(t => t.tag === modalCategory?.categoryTag);
                      return selectedTag?.description || 'Category tag for this requirement';
                    })()}
                  </small>
                </div>
                <div className="mb-2">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={modalCategory?.name || ''}
                    onChange={e => setModalCategory(c => c ? { ...c, name: e.target.value } : null)}
                    placeholder="e.g., Ethics and Professionalism"
                  />
                  <small className="text-muted">Display name for this category</small>
                </div>
                <div className="mb-2">
                  <label className="form-label">Min Credits</label>
                  <input
                    type="number"
                    className="form-control"
                    value={modalCategory?.minCredits ?? 0}
                    onChange={e => setModalCategory(c => c ? { ...c, minCredits: Number(e.target.value) } : null)}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Max Credits</label>
                  <input
                    type="number"
                    className="form-control"
                    value={modalCategory?.maxCredits ?? ''}
                    onChange={e => setModalCategory(c => c ? { ...c, maxCredits: e.target.value ? Number(e.target.value) : undefined } : null)}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Description</label>
                  <input
                    className="form-control"
                    value={modalCategory?.description || ''}
                    onChange={e => setModalCategory(c => c ? { ...c, description: e.target.value } : null)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={async () => {
                    if (!modalCategory) {
                      setModalError('Category data is missing');
                      return;
                    }
                    if (modalMode === 'add' && (!selectedStateID || !selectedProfessionID)) {
                      setModalError('Please select a state and profession first');
                      return;
                    }
                    setModalError('');
                    try {
                      if (modalMode === 'add' && selectedStateID && selectedProfessionID) {
                        // Find or create the requirement doc for this state/profession
                        let req = (groupedRequirements[selectedStateID]?.[selectedProfessionID] || [])[0];
                        if (!req) {
                          // Create new requirement doc with sensible defaults
                          const newReq = await createStateRequirement({
                            stateID: selectedStateID,
                            professionID: selectedProfessionID,
                            categoryIDs: [modalCategory],
                            cycleLengthYears: 2, // Default to 2-year cycle
                            totalCreditsRequired: Number(modalCategory.minCredits) || 0, // Use category min as starting point
                            effectiveDate: new Date().toISOString().split('T')[0], // Today's date
                          });
                          setRequirements(prev => [...prev, newReq]);
                        } else {
                          // Add to existing
                          const updatedCategories = [...req.categoryIDs, modalCategory];
                          const updatedReq = await updateStateRequirement(req._id!, { categoryIDs: updatedCategories });
                          setRequirements(prev => prev.map(r => r._id === req._id ? updatedReq : r));
                        }
                      } else if (modalMode === 'edit' && modalReqId) {
                        // Update the category directly via API
                        if (!modalCategory._id) throw new Error('Category ID missing');
                        
                        console.log('Updating category with ID:', modalCategory._id);
                        console.log('Update data:', {
                          name: modalCategory.name,
                          categoryTag: modalCategory.categoryTag,
                          minCredits: modalCategory.minCredits,
                          maxCredits: modalCategory.maxCredits,
                          description: modalCategory.description
                        });
                        
                        const updatedCategory = await updateCategory(modalCategory._id, {
                          name: modalCategory.name,
                          categoryTag: modalCategory.categoryTag,
                          minCredits: modalCategory.minCredits,
                          maxCredits: modalCategory.maxCredits,
                          description: modalCategory.description
                        });
                        
                        console.log('Category updated successfully:', updatedCategory);
                        
                        // Refresh the requirements to show updated category
                        const updatedReqs = await getAllStateRequirements();
                        setRequirements(updatedReqs);
                      }
                      closeModal();
                    } catch (err: any) {
                      console.error('Save error:', err);
                      setModalError(err.response?.data?.error || err.message || 'Save failed');
                    }
                  }}
                >Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && <div className="alert alert-danger mt-3">{error}</div>}
      {loading && <div>Loading...</div>}
    </div>
  );
}

export default StateManagement;
